import { Encounter } from "@medplum/fhirtypes";

import { Status } from "@/encounters/zod-schemas/encounters";

import { parseIsoInstant } from "./questionnaire-response-instant";

/**
 * Resolves when an encounter was signed and locked from its version history.
 *
 * Sign & Lock is the write that moves the Encounter to `finished`, and amendments keep it there, so
 * the signing instant is the `lastUpdated` of the version where the status became `finished`. Later
 * writes to a finished Encounter (billing tags, amendment details) add versions that are already
 * `finished` and leave that transition where it is. When an encounter becomes `finished` more than
 * once, the latest transition wins.
 *
 * @param encounterHistory Encounter versions, in any order.
 * @returns The ISO instant of the latest transition into `finished`, or undefined when no version
 * is `finished`.
 */
export function resolveEncounterSignedAt(encounterHistory: Encounter[]): string | undefined {
  const versionsOldestFirst = encounterHistory.reduce<
    { encounter: Encounter; instant: number; lastUpdated: string }[]
  >((versions, encounter) => {
    const lastUpdated = encounter.meta?.lastUpdated;
    const instant = parseIsoInstant(lastUpdated);
    if (lastUpdated && instant !== undefined) versions.push({ encounter, instant, lastUpdated });
    return versions;
  }, []);
  versionsOldestFirst.sort((first, second) => first.instant - second.instant);

  let signedAt: string | undefined;
  let wasFinished = false;
  for (const { encounter, lastUpdated } of versionsOldestFirst) {
    const isFinished = encounter.status === Status.Finished;
    if (isFinished && !wasFinished) signedAt = lastUpdated;
    wasFinished = isFinished;
  }

  return signedAt;
}
